import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import b from 'b_';
import ConcurrencyPopUpStore from '../../stores/ConcurrencyPopUpStore';
import SnackStore from '../../stores/SnackStore';
import PolicyModalStore, { ModalMode } from '../../stores/Policy/PolicyModalStore';
import CheckboxContainer from '../CheckboxContainer/CheckboxContainer';
import Card from '../Fuse/Card';
import FuseAlert from '../FuseControls/FuseAlert';
import FuseButton from '../FuseControls/FuseButton';
import FuseCheckbox from '../FuseControls/FuseCheckbox';
import FuseConsoleModal from '../ConsoleModal/FuseConsoleModal';
import FuseConfirmationModal from '../ConfirmationModal/FuseConfirmationModal';
import { FTBody } from '../Fuse';
import './PolicyModal.css';

const block = b.lock('PolicyModal');

class PolicyModal extends Component {
    static propTypes = {
        store: PolicyModalStore.PropType.isRequired,
        snack: SnackStore.PropType.isRequired,
        titleItemName: PropTypes.string.isRequired,
        subTitle: PropTypes.string,
        itemName: PropTypes.string, // noHeader - no need for itemName
        confirmDeleteMessage: PropTypes.func,
        confirmEditMessage: PropTypes.func,
        readOnly: PropTypes.bool,
    };

    onModalOpen = () => {
        if (this.props.store.mode === ModalMode.CREATE) {
            this.props.store.header.name.focus();
        }
    };

    isLoading = () => {
        const { store } = this.props;
        return store.status.isLoading && !store.actionSpinnerDisabled.getValue();
    };

    renderHeaderAction = () => {
        const { readOnly, store } = this.props;
        const {
            mode, setMode, hasChanged, deleteConfirmation, isValid,
            editConfirmation, requireEditConfirmation
        } = store;

        const commonProps = {
            primary: true,
            qa: 'policy-cta',
            style: { minWidth: 100 }
        };

        if (mode === ModalMode.VIEW && !readOnly) {
            return <FuseButton
                color="blue"
                icon="edit"
                onClick={() => setMode(ModalMode.EDIT)}
                {...commonProps}
            >Edit</FuseButton>;
        }
        if (mode === ModalMode.DELETE) {
            return <FuseButton
                color={'red'}
                disabled={!deleteConfirmation.getValue()}
                onClick={this.completeAction}
                {...commonProps}
            >{mode}</FuseButton>;
        }
        if (mode === ModalMode.EDIT || mode === ModalMode.CREATE) {
            return <FuseButton
                color={'green'}
                disabled={!isValid || !hasChanged()}
                loading={this.isLoading()}
                onClick={() => ((requireEditConfirmation() && mode === ModalMode.EDIT) ?
                    setMode(ModalMode.EDIT_CONFIRM) : this.completeAction())}
                qa="policy-cta"
            >Save</FuseButton>;
        }
        if (mode === ModalMode.EDIT_CONFIRM) {
            return <Fragment>
                <FuseButton
                    style={{ marginRight: 18 }}
                    color={'blue'}
                    onClick={() => setMode(ModalMode.EDIT)}
                    qa="policy-return-to-edit"
                >Return to Edit</FuseButton>
                <FuseButton
                    color={'green'}
                    disabled={!editConfirmation.isTrue}
                    loading={this.isLoading()}
                    onClick={this.completeAction}
                    qa="policy-cta"
                >Confirm</FuseButton>
            </Fragment>;
        }
    };

    onActionSuccess = (res, mode) => {
        if (res === ConcurrencyPopUpStore.CONCURRENCY_MODAL_CANCEL_LABEL) {
            return;
        }
        const { snack, store, titleItemName } = this.props;

        const itemName = res && store.preprocessHeaderValue
            ? store.preprocessHeaderValue(res)
            : this.getItemName();

        snack.showSuccess(
            <Fragment>{titleItemName} <b>{itemName}</b> has been {this.getActionName(mode)}.</Fragment>);
    }

    onActionFail = (mode) => {
        const { snack, titleItemName } = this.props;
        snack.showError(
            <Fragment>{titleItemName} <b>{this.getItemName()}</b> {this.getActionName(mode)} failed.</Fragment>);
    }

    getItemName = () => {
        const { store } = this.props;
        return store.subTitle !== null ? store.subTitle : store.header.name.value;
    }

    getActionName = (mode) => ({
        [ModalMode.DELETE]: 'deleted',
        [ModalMode.CREATE]: 'added',
        [ModalMode.EDIT]: 'saved',
    })[mode]

    completeAction = (force = false) => {
        const { store } = this.props;
        const mode = store.mode;

        return store.complete(force)
            .then((res) => this.onActionSuccess(res, mode))
            .catch(() => this.onActionFail(mode));
    };

    getTitle = () => {
        const { store, titleItemName } = this.props;

        return ({
            [ModalMode.CREATE]: 'Add ' + titleItemName,
            [ModalMode.EDIT]: 'Edit ' + titleItemName,
            [ModalMode.DELETE]: 'Delete ' + titleItemName,
            [ModalMode.EDIT_CONFIRM]: 'Review Changes',
            [ModalMode.VIEW]: titleItemName
        })[store.mode];
    };

    confirmDeleteMessage = () => {
        const { store, titleItemName, confirmDeleteMessage } = this.props;
        const { deleteConfirmation } = store;

        if (confirmDeleteMessage) {
            return <div className={block('DeleteConfirmation')}>
                {confirmDeleteMessage(deleteConfirmation)}
            </div>;
        }
        return <FuseAlert
            type="danger"
            title="Delete Confirmation"
            className={block('DeleteConfirmation')}
        >
            <FTBody className={block('DeleteModalAlertMessage')}>
                {titleItemName}s cannot be restored once deleted.
            </FTBody>
            <FuseCheckbox
                black
                store={deleteConfirmation}
                label={`Confirm the deletion of this ${titleItemName}`}
                qa={'confirm-delete'}
            />
        </FuseAlert>;
    };

    onConfirmEditCancelFn = () => {
        const { modal, cancelEditConfirmation } = this.props.store;
        cancelEditConfirmation.hide();
        modal.hide();
    };

    onConfirmEditCancel = () => {
        const { cancelEditConfirmation } = this.props.store;

        return <FuseConfirmationModal
            store={cancelEditConfirmation}
            onConfirm={this.onConfirmEditCancelFn}
        />;
    };

    confirmEditMessage() {
        const { store, confirmEditMessage } = this.props;

        if (confirmEditMessage) {
            return confirmEditMessage(store.editConfirmation);
        }
        return <Card title="Confirm Changes">
            <FTBody>Review changes to IP addresses and URL patterns before confirming.</FTBody>
            <CheckboxContainer className={block('ConfirmChangesCheckboxContainer')}>
                <FuseCheckbox
                    label={'Confirm changes to IP addresses and URL patterns'}
                    store={store.editConfirmation}
                    qa={'confirm-edit'}
                />
            </CheckboxContainer>
        </Card>;
    }

    overwrite = () => {
        this.completeAction(true);
    }

    render() {
        const { store, children, subTitle } = this.props;
        const { modal, mode, cancelEditConfirmation, hasChanged } = store;
        const canDisplayConfirmCancelModal = mode === ModalMode.EDIT || mode === ModalMode.CREATE;

        return <FuseConsoleModal
            isOpen={modal.isOpen}
            onClose={() =>
                (canDisplayConfirmCancelModal && hasChanged() ? cancelEditConfirmation.show() : modal.close())}
            onAfterOpen={this.onModalOpen}
            title={this.getTitle()}
            subTitle={subTitle || store.getSubTitle()}
            className={block()}
            hideCloseButton={mode === ModalMode.EDIT_CONFIRM}
            headerActions={this.renderHeaderAction()}
        >
            {mode === ModalMode.DELETE ? this.confirmDeleteMessage() : null}
            {canDisplayConfirmCancelModal ? this.onConfirmEditCancel() : null}

            {store.shouldShowEditConfirm() ? this.confirmEditMessage() : null}

            {children}

        </FuseConsoleModal>;
    }
}

export default inject('snack')(observer(PolicyModal));
